import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormField, ImageRef } from '../models/form.model';
import { FormValidationService } from '../service/form-validation.service';
import { ImageUploadModule } from '../../image-upload/image-upload.module';
import { ImageMetadata } from '../../image-upload/model/image-metadata.model';

@Component({
  selector: 'app-form-picture-upload-field',
  standalone: true,
  imports: [CommonModule, ImageUploadModule],
  template: `
    <app-image-upload
      [imageWidth]="getImageWidth()"
      [imageHeight]="getImageHeight()"
      [aspectRatio]="getAspectRatio()"
      [acceptedFileTypes]="getAcceptedFileTypes()"
      [outputFormat]="getOutputFormat()"
      [maxFileSize]="getMaxFileSize()"
      [label]="field.label"
      [helpText]="field.helpText"
      [required]="field.required"
      [organizationId]="organizationId"
      [projectId]="projectId"
      [context]="'form'"
      [existingImageUrl]="value?.url"
      (imageUploaded)="onImageUploaded($event)"
      (imageDeleted)="onImageDeleted($event)"
      (imageCropped)="onImageCropped($event)">
    </app-image-upload>
  `,
  styles: []
})
export class FormPictureUploadFieldComponent implements OnChanges {
  @Input() field!: FormField;
  @Input() value: ImageRef | undefined;
  @Input() organizationId?: string;
  @Input() projectId?: string;
  @Output() valueChange = new EventEmitter<ImageRef>();
  @Output() errorChange = new EventEmitter<any>();

  constructor(public validationService: FormValidationService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value']) {
      console.log('[PictureUploadFieldComponent] Value changed:', this.value);
      
      // If we have a value but it's missing the URL (incomplete data from server),
      // try to retrieve it from sessionStorage
      if (this.value && !this.value.url && this.field?.id) {
        const cachedValue = sessionStorage.getItem(`imageField_${this.field.id}`);
        if (cachedValue) {
          try {
            const cachedImageRef = JSON.parse(cachedValue);
            console.log('[PictureUploadFieldComponent] Retrieved image from cache:', cachedImageRef);
            this.value = cachedImageRef;
          } catch (e) {
            console.error('[PictureUploadFieldComponent] Failed to parse cached image:', e);
          }
        }
      }
    }
  }

  getImageWidth(): number {
    return this.field.imageConfig?.imageWidth || 400;
  }

  getImageHeight(): number | undefined {
    return this.field.imageConfig?.imageHeight;
  }

  getAspectRatio(): string {
    return this.field.imageConfig?.aspectRatio || 'free';
  }

  getAcceptedFileTypes(): string[] {
    return this.field.imageConfig?.acceptedFileTypes || ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
  }

  getOutputFormat(): string {
    return this.field.imageConfig?.outputFormat || 'png';
  }

  getMaxFileSize(): number {
    return this.field.imageConfig?.maxFileSize || 5242880; // 5MB default
  }

  onImageUploaded(metadata: ImageMetadata): void {
    const imageRef: ImageRef = {
      fileId: metadata.id || '',
      filename: metadata.filename,
      url: metadata.imageUrl,
      contentType: metadata.mimeType,
      size: metadata.fileSize
    };
    
    console.log('[PictureUploadFieldComponent] onImageUploaded - Complete ImageRef:', imageRef);
    
    // Cache the complete image ref in sessionStorage so we can retrieve it later
    // if the backend only returns partial data
    if (this.field?.id) {
      sessionStorage.setItem(`imageField_${this.field.id}`, JSON.stringify(imageRef));
      console.log(`[PictureUploadFieldComponent] Cached image for field ${this.field.id}:`, imageRef);
    }
    
    this.value = imageRef;
    console.log('[PictureUploadFieldComponent] About to emit valueChange with:', imageRef);
    this.valueChange.emit(imageRef);
    this.errorChange.emit(null);
  }

  onImageDeleted(imageId: string): void {
    // Clear the cache for this field
    if (this.field?.id) {
      sessionStorage.removeItem(`imageField_${this.field.id}`);
    }
    this.value = undefined;
    this.valueChange.emit(undefined as any);
  }

  onImageCropped(blob: Blob): void {
    // Can be used for additional validation or preview if needed
  }
}
